import { format } from "date-fns";
import { Box, Button, Grid, ResponsiveContext, Table, TableBody, TableCell, TableHeader, TableRow, Text, Tip } from "grommet";
import { every, some } from "lodash";
import * as React from "react";
import ReactMarkdown from "react-markdown";
import AnswerableOption from "./AnswerableOption";

import { FcComments, FcFile, FcPlus } from "react-icons/fc";
import { HiExclamationCircle, HiTrash } from "react-icons/hi";
import remarkGfm from "remark-gfm";
import { Account, useUser } from "../utils/AccountStore";
import { Comment, IQuestionnaireInstance, Question, Questionnaire, getSubmissionData, validate } from "../utils/ServiceManagerAPI";
import { useDarkMode } from "./DarkModeSwitch";
import RenderAttachments from "./RenderAttachments";
import StyledModal from "./StyledModel";
import rehypeRaw from "rehype-raw";

export interface QuestionDataValidationAPIModel {
    id: string;
    questionID: string;
    name: string;
    range: string;
    regularExpression: string;
    length: string;
    evaluate: string;
    optionsAppliedTo: string[];
}

export const markdownComponents = {
    table: (props) => <Table {...props} />,
    tr: (props) => <TableRow {...props} />,
    td: (props) => <TableCell border="all" style={{ textAlign: "center" }} {...props} />,
    th: (props) => <TableCell border="all" style={{ textAlign: "center" }} {...props} />,
    tbody: (props) => <TableBody {...props} />,
    thead: (props) => <TableHeader {...props} />,
};

interface AnswerableQuestionProps {
    loading: boolean;
    currentQuestionnaire?: Questionnaire;
    question: Question;
    handleOptionChange: Function;
    deleteSet?: Function;
    addSet?: Function;
    toggleAttachmentModal?: Function;
    toggleCommentModal?: Function;
    deleteAttachment?: Function;
    deleteComment?: Function;
    similarQuestionnaires: Questionnaire[];
    questionnaires: IQuestionnaireInstance[];
}

export const AnswerableQuestion = ({
    loading,
    currentQuestionnaire,
    question,
    handleOptionChange,
    deleteSet,
    addSet,
    toggleAttachmentModal,
    toggleCommentModal,
    deleteAttachment,
    deleteComment,
    similarQuestionnaires,
    questionnaires,
}: AnswerableQuestionProps) => {
    const size = React.useContext(ResponsiveContext);

    const [valid, setValid] = React.useState(question.dataValidations?.length == 0);
    const [validationMessage, setValidationMessage] = React.useState("");
    const { user, setUser } = useUser();

    const doValidation = (overrideOptionValue?, overrideOptionId?) => {
        let valid = true;
        if (currentQuestionnaire && question.dataValidations) {
            let messages = [] as string[];
            question.dataValidations.forEach((validation) => {
                question.options?.forEach((option) => {
                    if (!validation.optionsAppliedTo || validation.optionsAppliedTo.length == 0 || validation.optionsAppliedTo.includes(option.id)) {
                        let test = validate(validation, option.value, question, getSubmissionData(currentQuestionnaire));
                        if (overrideOptionId && option.id == overrideOptionId) {
                            test = overrideOptionValue;
                        }
                        valid = valid && test;
                        if (!test) {
                            messages.push(validation.name);
                        }
                    }
                });
            });
            setValidationMessage(messages.join(", "));
        }
        setValid(valid);
    };

    React.useEffect(() => {
        doValidation();
    }, [question]);

    const [isDarkMode] = useDarkMode();

    const useGridLayout = currentQuestionnaire?.questionnaireType?.useGridLayout || false;

    const getQuestionColor = () => {
        return `var(${isDarkMode ? "--question-color-dark" : "--question-color-light"},${isDarkMode ? "var(--heading-color-dark)" : "var(--heading-color-light)"})`;
    };

    return (
        <Box>
            <Grid columns={useGridLayout ? ["1/4", "3/4"] : { size: "auto", count: 1 }}>
                <Box>
                    {/* Question Text, AutoFill and Links */}
                    <Box>
                        <Box direction="row" gap="small" fill>
                            <Text size="large" color={getQuestionColor()}>
                                {question.text}
                                {question.required && (
                                    <Text style={{ padding: "0.1rem" }} color={question.answers?.length != 0 ? "background-front" : "status-critical"}>
                                        *
                                    </Text>
                                )}
                            </Text>
                            {!valid && (
                                <Text>
                                    <Tip content={validationMessage}>
                                        <HiExclamationCircle color="red" />
                                    </Tip>
                                </Text>
                            )}
                            <Box justify="center">
                                {/* {addSet && currentQuestionnaire?.questionnaireType?.allowAnswerCopying && !currentQuestionnaire.locked && questionnaires?.length == 1 && (
                                    <CopyAnswer question={question} similarQuestionnaires={similarQuestionnaires} handleOptionChange={handleOptionChange} addSet={addSet}></CopyAnswer>
                                )} */}
                            </Box>
                        </Box>
                    </Box>
                    {/* 
                    {question.tags && question.tags.length > 0 && (
                        <QuestionnaireTypeTags size={"xsmall"} tags={question.tags.map((t) => t.tag)} selectedTags={question.tags.map((t) => t.tag)}></QuestionnaireTypeTags>
                    )} */}
                    {question.subtext && (
                        <Box pad={"small"}>
                            <ReactMarkdown remarkPlugins={[remarkGfm]} rehypePlugins={[rehypeRaw]} components={markdownComponents}>
                                {question.subtext}
                            </ReactMarkdown>
                        </Box>
                    )}
                </Box>

                {(!questionnaires || questionnaires.length == 1) && (
                    <Box>
                        <Grid columns={size == "small" || !currentQuestionnaire?.allowComments ? ["1fr"] : ["3fr", "2fr"]}>
                            <RenderQuestionOptions
                                questionnaire={currentQuestionnaire}
                                question={question}
                                handleOptionChange={handleOptionChange}
                                loading={loading}
                                deleteSet={deleteSet}
                                addSet={addSet}
                                disabled={false}
                                validate={true}
                                onValidate={doValidation}
                            ></RenderQuestionOptions>
                            <RenderQuestionCommentsAndUploads
                                currentQuestionnaire={currentQuestionnaire}
                                question={question}
                                user={user}
                                deleteComment={deleteComment}
                                toggleCommentModal={toggleCommentModal}
                                toggleAttachmentModal={toggleAttachmentModal}
                                deleteAttachment={deleteAttachment}
                                questionnaires={questionnaires}
                            ></RenderQuestionCommentsAndUploads>
                        </Grid>
                        {/* {!currentQuestionnaire?.locked && question.allowUploads && currentQuestionnaire?.allowUploads && user.hasQuestionnaireAccess(currentQuestionnaire.id, 8) && (
                            <Box align="center" direction="row" gap="small">
                                <AttachmentUpload questionId={question.id} questionnaireId={currentQuestionnaire?.id} done={attachmentUploaded}></AttachmentUpload>
                            </Box>
                        )} */}
                    </Box>
                )}
            </Grid>
        </Box>
    );
};

function RenderQuestionCommentsAndUploads({
    currentQuestionnaire,
    question,
    user,
    deleteComment,
    toggleCommentModal,
    toggleAttachmentModal,
    deleteAttachment,
    questionnaires,
}: {
    currentQuestionnaire?: Questionnaire;
    question: Question;
    user: Account;
    deleteComment?: Function;
    toggleCommentModal?: Function;
    toggleAttachmentModal?: Function;
    deleteAttachment?: Function;
    questionnaires: IQuestionnaireInstance[];
}) {
    return (
        <Box>
            {question.allowComments && (
                <RenderQuestionComments
                    currentQuestionnaire={currentQuestionnaire}
                    question={question}
                    user={user}
                    deleteComment={deleteComment}
                    questionnaires={questionnaires}
                ></RenderQuestionComments>
            )}
            {question.allowUploads && (
                <RenderAttachments questionnaire={currentQuestionnaire} attachments={question?.attachments || []} user={user} deleteAttachment={deleteAttachment}></RenderAttachments>
            )}
            {!currentQuestionnaire?.locked && (
                <Box align="center" direction="row" gap="small" justify="end">
                    {question.allowComments && currentQuestionnaire?.allowComments && user.hasQuestionnaireAccess(currentQuestionnaire?.id, 16) && (
                        <Box round="4px" pad="xsmall" direction="row" gap={"xsmall"} hoverIndicator="background-front" onClick={() => toggleCommentModal && toggleCommentModal(question.id)}>
                            <Text>Add Comment</Text>
                            <FcComments size="24px" />
                        </Box>
                    )}
                    {question.allowUploads && currentQuestionnaire?.allowUploads && (
                        <Tip content="Add Files">
                            <Box round="4px" pad="xsmall" direction="row" gap={"xsmall"} hoverIndicator="background-front" onClick={() => toggleAttachmentModal && toggleAttachmentModal(question.id)}>
                                <Text>Add Files</Text>
                                <FcFile size="24px" />
                            </Box>
                        </Tip>
                    )}
                </Box>
            )}
        </Box>
    );
}

function RenderQuestionComments({
    questionnaires,
    currentQuestionnaire,
    question,
    user,
    deleteComment,
}: {
    questionnaires: IQuestionnaireInstance[];
    currentQuestionnaire?: Questionnaire;
    question: Question;
    user: Account;
    deleteComment?: Function;
}) {
    const size = React.useContext(ResponsiveContext);
    const [showDeleteModal, setShowDeleteModal] = React.useState(false);

    let comments = questionnaires
        ?.flatMap((questionnaire) => questionnaire.questionnaire.questions)
        ?.flatMap((question) => question?.comments)
        ?.filter((c) => c && c?.questionID == question.id);

    const [commentToDelete, setCommentToDelete] = React.useState<Comment>();
    const [loading, setLoading] = React.useState(false);

    const doDelete = async () => {
        setLoading(true);
        if (commentToDelete?.id && deleteComment) {
            await deleteComment(question.id, commentToDelete.id);
        }
        setShowDeleteModal(false);
        setLoading(false);
    };

    return (
        <>
            {question.allowComments && (user.hasQuestionnaireAccess(currentQuestionnaire?.id, 16) || user.hasCampaignAccess(currentQuestionnaire?.campaignID, 16)) && (
                <Box gap="small" pad={{ left: "small" }}>
                    {comments?.map((comment, commentKey) => (
                        <Box key={`q${question.id}c${commentKey}`}>
                            <Grid columns={size == "small" ? ["1fr"] : ["150px", "auto", "40px"]}>
                                <Box>
                                    <Text size="small">{comment.username}</Text>
                                    {comment.timestamp && <Text size="xsmall">{format(comment.timestamp, "dd/MM/yyyy HH:mm")}</Text>}
                                </Box>
                                <Text size="small">{comment.text}</Text>
                                <Box align="center">
                                    {!currentQuestionnaire?.locked && deleteComment && comment.questionnaireID == currentQuestionnaire?.id && user.id == comment.userID && (
                                        <Tip content="Delete Comment">
                                            <Button
                                                color="status-critical"
                                                icon={<HiTrash size="20px" />}
                                                onClick={() => {
                                                    setShowDeleteModal(true);
                                                    setCommentToDelete(comment);
                                                }}
                                                label=""
                                            ></Button>
                                        </Tip>
                                    )}
                                </Box>
                            </Grid>
                        </Box>
                    ))}
                    <StyledModal
                        isOpen={showDeleteModal}
                        setIsOpen={() => {
                            setShowDeleteModal(false);
                            setCommentToDelete(undefined);
                        }}
                    >
                        <Box width={"medium"}>
                            <Text>Are you sure you want to delete this comment?</Text>
                            <Box direction="row" justify="end" pad={"small"} gap="small">
                                <Button onClick={() => setShowDeleteModal(false)} label="Cancel"></Button>
                                {commentToDelete && <Button primary color="status-critical" onClick={() => doDelete()} disabled={loading} label="Confirm"></Button>}
                            </Box>
                        </Box>
                    </StyledModal>
                </Box>
            )}
        </>
    );
}

interface RenderQuestionOptionsProps {
    questionnaire?: Questionnaire;
    question: Question;
    handleOptionChange: Function;
    loading: boolean;
    deleteSet?: Function;
    addSet?: Function;
    disabled: boolean;
    validate: boolean;
    onValidate: Function;
}

function RenderQuestionOptions({ questionnaire, question, handleOptionChange, loading, deleteSet, addSet, disabled, validate, onValidate }: RenderQuestionOptionsProps) {
    return (
        <>
            {question.allowSets && (
                <Box>
                    {question.sets?.map((set, setKey) => (
                        <Box gap="xsmall" direction="row" key={`q${question.id}s${setKey}`}>
                            <Box>
                                <QuestionOptions
                                    question={question}
                                    options={set.options}
                                    handleOptionChange={handleOptionChange}
                                    setId={set.id}
                                    disabled={questionnaire?.locked || disabled}
                                    onValidate={onValidate}
                                    validate={validate}
                                    gridView={!!questionnaire?.questionnaireType?.useGridLayout}
                                ></QuestionOptions>
                            </Box>
                            {deleteSet && (
                                <Box>
                                    <Tip content="Delete Set" dropProps={{ align: { left: "right" } }}>
                                        <Box round="small" pad="small" justify="center" hoverIndicator={{ background: "status-error" }} onClick={() => deleteSet(question.id, set.id)}>
                                            x
                                        </Box>
                                    </Tip>
                                </Box>
                            )}
                        </Box>
                    ))}
                    {addSet && (
                        <Box direction="row">
                            <Button
                                disabled={questionnaire?.locked || loading}
                                label="Add an answer"
                                style={{ borderRadius: "4px" }}
                                icon={<FcPlus size="24px" />}
                                size="small"
                                primary
                                color="status-ok"
                                onClick={() => addSet(question.id)}
                            ></Button>
                        </Box>
                    )}
                </Box>
            )}
            {!question.allowSets && (
                <>
                    <Box>
                        <QuestionOptions
                            question={question}
                            options={question.options || []}
                            disabled={questionnaire?.locked || disabled}
                            handleOptionChange={handleOptionChange}
                            setId={null}
                            onValidate={onValidate}
                            validate={validate}
                            gridView={!!questionnaire?.questionnaireType?.useGridLayout}
                        ></QuestionOptions>
                    </Box>
                </>
            )}
        </>
    );
}

interface QuestionOptionsProps {
    question: Question;
    options: any[];
    handleOptionChange: Function;
    setId: string | null;
    disabled: boolean;
    onValidate: Function;
    validate: boolean;
    gridView: boolean;
}

function QuestionOptions({ question, options, handleOptionChange, setId, disabled, onValidate, validate, gridView }: QuestionOptionsProps) {
    let onlySelections = every(options, (o) => o.type === 0);
    let atomic = some(options, (o) => o.type === 0) && (!every(options, (o) => o.type === 0) || options.length == 1);
    let gapSize = "xsmall";
    {
        !atomic ? (gapSize = "xsmall") : (gapSize = "large");
    }

    let longTextOnly = every(options, (o) => o.type === 7);

    return (
        <Box pad={{ left: onlySelections ? "small" : "" }}>
            <Box direction={longTextOnly || options.length == 1 ? "column" : "row"} gap={gapSize} wrap={!longTextOnly}>
                {options.map((option, optionKey) => (
                    <AnswerableOption
                        question={question}
                        key={`q${question.id}o${optionKey}`}
                        style={
                            {
                                // gridColumn: OptionTypeGridSpan(option, question),
                            }
                        }
                        option={option}
                        value={option.value}
                        changed={(value) => handleOptionChange(value, option, question.id, setId)}
                        atomic={atomic}
                        disabled={disabled}
                        onValidate={onValidate}
                        validate={validate}
                    ></AnswerableOption>
                ))}
            </Box>
            {!disabled && (question?.options || []).length > 0 && !gridView && (
                <>
                    {question.multipleOptions &&
                        every(question.options, {
                            type: 0,
                        }) && (
                            <Text color="dark-4" size="small">
                                Choose multiple
                            </Text>
                        )}
                    {!question.multipleOptions && every(question.options, { type: 0 }) && (
                        <Text color="dark-4" size="small">
                            Choose one
                        </Text>
                    )}
                </>
            )}
        </Box>
    );
}

export default AnswerableQuestion;
